import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Spinner } from '../../components/ui/Spinner.js';
import { type HistoryFilter, type HistoryRecord, useSettingsHistory } from './settingsApi.js';
import {
  GROUP_LABELS,
  SETTING_LABELS,
  type SettingKey,
  type SettingsGroup,
  changeParts,
  formatMyt,
} from './settingsFormat.js';

const FILTERS: Array<{ id: SettingsGroup | 'system' | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'money', label: GROUP_LABELS.money },
  { id: 'booking_rules', label: GROUP_LABELS.booking_rules },
  { id: 'automation', label: GROUP_LABELS.automation },
  { id: 'legal', label: GROUP_LABELS.legal },
  { id: 'app', label: GROUP_LABELS.app },
  { id: 'notifications', label: GROUP_LABELS.notifications },
  { id: 'system', label: 'By the system' },
];

function Row({ record }: { record: HistoryRecord }) {
  const label = SETTING_LABELS[record.setting as SettingKey] ?? record.setting;
  return (
    <tr className="border-t border-hairline-soft align-top" data-testid="history-row">
      <td className="whitespace-nowrap px-4 py-2.5 text-slate">{formatMyt(record.at)}</td>
      <td className="px-4 py-2.5 font-semibold text-ink">{label}</td>
      <td className="px-4 py-2.5 text-slate">
        <ul className="flex flex-col gap-0.5">
          {record.changes.map((c) => {
            const p = changeParts(record.setting, c);
            return (
              <li key={c.field}>
                {p.label}: <s className="text-muted">{p.from}</s> → {p.to}
              </li>
            );
          })}
        </ul>
      </td>
      <td className="px-4 py-2.5">
        {record.actor.kind === 'system' ? (
          <span className="flex flex-col">
            <span className="inline-flex w-max rounded bg-surface-feature px-1.5 text-xs font-semibold text-primary-deep">
              System
            </span>
            {record.actor.reason && (
              <span className="text-xs text-steel">{record.actor.reason}</span>
            )}
          </span>
        ) : (
          <span className="text-charcoal">{record.actor.name ?? 'A staff member'}</span>
        )}
      </td>
    </tr>
  );
}

/** 356 (FR-018): every settings change, newest first, filtered by group or by the system. */
export function HistoryTab() {
  const [params, setParams] = useSearchParams();
  const groupParam = params.get('group');
  const active: (typeof FILTERS)[number]['id'] = FILTERS.some((f) => f.id === groupParam)
    ? (groupParam as (typeof FILTERS)[number]['id'])
    : 'all';
  const filter: HistoryFilter =
    active === 'system' ? { system: true } : active === 'all' ? {} : { group: active };
  const [page, setPage] = useState(1);
  const history = useSettingsHistory(filter, page);

  function choose(id: (typeof FILTERS)[number]['id']) {
    setPage(1);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'all') next.delete('group');
        else next.set('group', id);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">Filter history</legend>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={active === f.id}
            onClick={() => choose(f.id)}
            className={[
              'h-8 rounded-full border px-3 text-sm',
              active === f.id
                ? 'border-ink bg-ink text-canvas'
                : 'border-hairline-strong text-charcoal hover:bg-surface',
            ].join(' ')}
          >
            {f.label}
          </button>
        ))}
      </fieldset>
      {history.isLoading && (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      )}
      {history.isError && (
        <p className="text-sm text-semantic-danger-text">
          The history could not be loaded. Please try again shortly.
        </p>
      )}
      {history.data && history.data.items.length === 0 && (
        <p data-testid="history-empty" className="text-sm text-steel">
          No changes recorded{active === 'all' ? ' yet' : ' for this filter'}. History starts with
          the first change after this release.
        </p>
      )}
      {history.data && history.data.items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-hairline">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs font-semibold uppercase tracking-wide text-steel">
              <tr>
                <th className="px-4 py-2.5">When (MYT)</th>
                <th className="px-4 py-2.5">Setting</th>
                <th className="px-4 py-2.5">Change</th>
                <th className="px-4 py-2.5">By</th>
              </tr>
            </thead>
            <tbody>
              {history.data.items.map((r) => (
                <Row key={r.id} record={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {history.data && history.data.pages > 1 && (
        <div className="flex items-center justify-between text-sm text-steel">
          <span>
            Page {history.data.page} of {history.data.pages}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-hairline-strong px-3 py-1 disabled:opacity-50"
            >
              Newer
            </button>
            <button
              type="button"
              disabled={page >= history.data.pages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-hairline-strong px-3 py-1 disabled:opacity-50"
            >
              Older
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
